/**
 * KANGA TCG — promo popup.
 *
 * The <dialog> element already handles the modal behaviour that is easy to get
 * wrong: backdrop, Escape to close, focus trapped while open and restored on
 * close. This file only decides *when* it opens, remembers that the visitor
 * closed it, and holds the video back until it is actually on screen.
 */
(function () {
  'use strict';

  var DAY = 86400000;
  var WINDOWS = { session: 0, day: DAY, week: DAY * 7 };

  /**
   * Whether the visitor has closed this popup recently enough to be left alone.
   * Storage is best-effort: in private mode, or with storage blocked, this
   * throws, and the popup simply shows — better than not showing at all.
   */
  function suppressed(popup) {
    var frequency = popup.dataset.frequency || 'session';
    if (frequency === 'always') return false;

    var key = popup.dataset.storageKey;

    try {
      if (frequency === 'session') {
        return sessionStorage.getItem(key) !== null;
      }

      var closedAt = parseInt(localStorage.getItem(key), 10);
      if (!closedAt) return false;
      return Date.now() - closedAt < WINDOWS[frequency];
    } catch (e) {
      return false;
    }
  }

  function remember(popup) {
    var frequency = popup.dataset.frequency || 'session';
    if (frequency === 'always') return;

    var key = popup.dataset.storageKey;

    try {
      if (frequency === 'session') {
        sessionStorage.setItem(key, '1');
      } else {
        localStorage.setItem(key, String(Date.now()));
      }
    } catch (e) {
      /* Storage unavailable — the popup will show again next time. */
    }
  }

  /** Start the media only once the popup is actually open. */
  function startMedia(popup) {
    var iframe = popup.querySelector('iframe[data-src]');
    if (iframe) {
      iframe.src = iframe.dataset.src;
      delete iframe.dataset.src;
    }

    var video = popup.querySelector('video');
    if (video) {
      // Autoplay is only permitted while muted; the sound button restores
      // audio on a real user gesture.
      video.muted = true;
      video.setAttribute('playsinline', '');
      var attempt = video.play();
      if (attempt && attempt.catch) attempt.catch(function () {});
    }
  }

  function stopMedia(popup) {
    var video = popup.querySelector('video');
    if (video) video.pause();

    // Detaching the iframe src is what actually stops a YouTube or Vimeo
    // player; pausing it is not something the parent page can do.
    var iframe = popup.querySelector('iframe[src]');
    if (iframe) {
      iframe.dataset.src = iframe.src;
      iframe.removeAttribute('src');
    }
  }

  function open(popup) {
    if (popup.open) return;
    if (typeof popup.showModal !== 'function') return;

    popup.showModal();
    startMedia(popup);
  }

  function close(popup) {
    stopMedia(popup);
    remember(popup);
    if (popup.open) popup.close();
  }

  function setup(popup) {
    if (popup.dataset.kangaPopupReady) return;
    popup.dataset.kangaPopupReady = 'true';

    popup.querySelectorAll('[data-kanga-popup-close]').forEach(function (button) {
      button.addEventListener('click', function () {
        close(popup);
      });
    });

    // Escape fires the dialog's own close event rather than our handler, so
    // the visitor's choice is recorded there too.
    popup.addEventListener('close', function () {
      stopMedia(popup);
      remember(popup);
    });

    // A click landing on the dialog itself is a click on the backdrop — the
    // panel inside it covers every other pixel.
    popup.addEventListener('click', function (event) {
      if (event.target === popup) close(popup);
    });

    var sound = popup.querySelector('[data-kanga-popup-sound]');
    var video = popup.querySelector('video');
    if (sound && video) {
      sound.addEventListener('click', function () {
        video.muted = !video.muted;
        sound.setAttribute('aria-pressed', String(!video.muted));
      });
    }

    // In the theme editor the popup opens immediately and ignores the
    // dismissal record, so the merchant can actually see what they are editing.
    if (popup.dataset.designMode === 'true') {
      open(popup);
      return;
    }

    if (suppressed(popup)) return;

    var delay = parseInt(popup.dataset.delay, 10) || 0;
    setTimeout(function () {
      open(popup);
    }, delay);
  }

  function init() {
    document.querySelectorAll('[data-kanga-popup]').forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify re-renders a section when its settings change in the editor.
  document.addEventListener('shopify:section:load', init);
})();
